import styled from "styled-components";

import { media } from "../styles/media";

export const AccountSettingsStyles = styled.section`
  min-height: calc(100vh - 42px);
  width: 100%;
  font-family: ${({ theme }) => theme.typography.fontFamily};

  /* Hero card (dark variant) */
  .hero {
    max-width: 960px;
    margin-top: ${({ theme }) => theme.spacing[24]};
    position: relative;
    overflow: hidden;
    border-radius: ${({ theme }) => theme.radius[12]};
    background: ${({ theme }) => theme.colors.semantic.container.white};
    border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    box-shadow: ${({ theme }) => theme.shadows.card};
  }

  .heroCover {
    position: relative;
    padding: 28px;
    background: radial-gradient(
        circle at 88% 0%,
        rgba(255, 255, 255, 0.1) 0,
        transparent 45%
      ),
      radial-gradient(
        circle at 12% 100%,
        rgba(255, 255, 255, 0.08) 0,
        transparent 50%
      ),
      linear-gradient(
        120deg,
        ${({ theme }) => theme.colors.accent[500]} 0%,
        ${({ theme }) => theme.colors.accent[700]} 60%,
        ${({ theme }) => theme.colors.accent[900]} 100%
      );
  }

  .heroRow {
    display: flex;
    align-items: center;
    gap: 22px;
  }

  .avatarWrap {
    position: relative;
    width: 96px;
    height: 96px;
    border-radius: 50%;
    flex-shrink: 0;
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.18),
      0 4px 14px rgba(0, 0, 0, 0.25);

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .avatarInit {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.accent[500]} 0%,
      ${({ theme }) => theme.colors.accent[700]} 100%
    );
    color: #fff;
    font-weight: 600;
    letter-spacing: 0.5px;
    font-size: 36px;
  }

  .avatarEdit {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.accent[500]};
    color: #fff;
    border: 2px solid #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.12s ease, transform 0.12s ease;
    padding: 0;

    &:hover {
      background: ${({ theme }) => theme.colors.accent[700]};
      transform: scale(1.06);
    }
  }

  .heroInfo {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .heroName {
    font-size: 28px;
    font-weight: 600;
    color: #fff;
    letter-spacing: -0.4px;
    line-height: 34px;
  }

  .heroTagline {
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-size: 14px;
  }

  .rolePillDark {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.22);
    color: #fff;
    font-weight: 500;
    border-radius: 999px;
    font-size: 13px;
    backdrop-filter: blur(6px);
  }

  .heroDot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.4);
    display: inline-block;
  }

  .heroLineLight {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.92);

    svg {
      color: rgba(255, 255, 255, 0.7);
    }
  }

  .heroActions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  .btnOnAccentGhost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    background: transparent;
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.32);
    transition: all 0.12s ease;
    white-space: nowrap;

    &:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(255, 255, 255, 0.5);
    }
  }

  .heroFoot {
    padding: 14px 28px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    flex-wrap: wrap;

    .heroLine {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: ${({ theme }) => theme.colors.semantic.text.weak};

      svg {
        color: ${({ theme }) => theme.colors.semantic.text.placeholder};
      }
    }

    .dot {
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background: ${({ theme }) => theme.colors.accent[200]};
      display: inline-block;
    }
  }

  /* Tab bar */
  .tabBar {
    max-width: 960px;
    margin-top: 20px;
    display: flex;
    gap: 4px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.semantic.separator};
  }

  .tabBtn {
    background: transparent;
    border: none;
    padding: 12px 16px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: color 0.12s ease, border-color 0.12s ease;
    user-select: none;
    -webkit-user-select: none;

    &:hover {
      color: ${({ theme }) => theme.colors.semantic.text.strong};
    }

    &.isActive {
      color: ${({ theme }) => theme.colors.accent[500]};
      border-color: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  /* Two-column content */
  .content {
    max-width: 960px;
    margin-top: 24px;
  }

  .grid {
    display: grid;
    grid-template-columns: 260px 1fr;
    gap: 32px;
    align-items: start;
  }

  .aside {
    position: sticky;
    top: 24px;
  }

  .asideTitle {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    letter-spacing: -0.1px;
  }

  .asideSub {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    margin-top: 6px;
    line-height: 19px;
  }

  .cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .cardSurface {
    background: ${({ theme }) => theme.colors.semantic.container.white};
    border-radius: ${({ theme }) => theme.radius[12]};
    border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    box-shadow: ${({ theme }) => theme.shadows.card};
    padding: 22px 24px;
  }

  .gridTwo {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 18px;

    &.narrow {
      max-width: 540px;
    }
  }

  .rowEnd {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 18px;
  }

  /* Section header */
  .secHead {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .secHeadLeft {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .secHeadIcon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.accent[50]};
    color: ${({ theme }) => theme.colors.accent[500]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .secHeadTitle {
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    letter-spacing: -0.1px;
    line-height: 20px;
  }

  .secHeadSub {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    margin-top: 2px;
    line-height: 18px;
  }

  /* Field */
  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .fieldLabelRow {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .fieldLabel {
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    letter-spacing: 0.2px;
  }

  .fieldControl {
    position: relative;
    display: flex;
    align-items: center;
  }

  .fieldLabelAccessory {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.success[600]};
    font-weight: 500;

    svg {
      color: ${({ theme }) => theme.colors.success[600]};
    }
  }

  .fieldHint {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
  }

  .input {
    width: 100%;
    height: 40px;
    padding: 0 14px;
    background: #fff;
    border: 1px solid ${({ theme }) => theme.colors.semantic.outline.default};
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    outline: none;
    transition: border-color 0.12s ease, box-shadow 0.12s ease;

    &::placeholder {
      color: ${({ theme }) => theme.colors.semantic.text.placeholder};
    }

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary[400]};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.accent[500]};
      box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.accent[50]};
    }

    &:disabled {
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.semantic.text.weak};
      cursor: not-allowed;
    }
  }

  .pwdWrap {
    position: relative;
    flex: 1;
    display: flex;
  }

  .pwdToggle {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.semantic.text.weak};

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  .selWrap {
    position: relative;
    flex: 1;
    display: flex;
  }

  .select {
    appearance: none;
    -webkit-appearance: none;
    padding-right: 36px;
    cursor: pointer;
    background: #fff;
  }

  .selCaret {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    pointer-events: none;
  }

  /* Toggle */
  .toggleList {
    margin-top: 8px;
  }

  .toggleRow {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    background: transparent;
    border: none;
    padding: 10px 0;
    cursor: pointer;
    text-align: left;
    font-family: inherit;
    border-radius: 8px;

    & + .toggleRow {
      border-top: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    }
  }

  .toggleText {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .toggleLabel {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
  }

  .toggleSub {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    line-height: 16px;
  }

  .toggleSwitch {
    flex-shrink: 0;
    width: 36px;
    height: 20px;
    border-radius: 999px;
    background: ${({ theme }) => theme.colors.primary[200]};
    position: relative;
    transition: background 0.15s ease;
  }

  .toggleThumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.18s cubic-bezier(0.5, 1.4, 0.6, 1);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  }

  .toggleRow.isOn .toggleSwitch {
    background: ${({ theme }) => theme.colors.accent[500]};
  }

  .toggleRow.isOn .toggleThumb {
    transform: translateX(16px);
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.12s ease;
    white-space: nowrap;
  }

  .btnPrimary {
    background: ${({ theme }) => theme.colors.accent[500]};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.accent[500]};

    &:hover {
      background: ${({ theme }) => theme.colors.accent[700]};
      border-color: ${({ theme }) => theme.colors.accent[700]};
    }
  }

  .btnGhost {
    background: #fff;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    border-color: ${({ theme }) => theme.colors.semantic.outline.default};

    &:hover {
      background: ${({ theme }) => theme.colors.accent[50]};
      color: ${({ theme }) => theme.colors.accent[500]};
      border-color: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  .btnDangerGhost {
    background: #fff;
    color: ${({ theme }) => theme.colors.error[500]};
    border-color: ${({ theme }) => theme.colors.semantic.outline.default};

    &:hover {
      background: ${({ theme }) => theme.colors.error[50]};
      border-color: ${({ theme }) => theme.colors.error[500]};
    }
  }

  .btnSm {
    padding: 6px 12px;
    font-size: 13px;
  }

  /* Role hero block (in Role tab) */
  .roleHero {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    padding: 16px 18px;
    border-radius: 12px;
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.colors.accent[50]} 0%,
      #fff 100%
    );
    border: 1px solid ${({ theme }) => theme.colors.accent[100]};
  }

  .roleIcon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.accent[500]};
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 22px;
      height: 22px;
    }
  }

  .roleName {
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
  }

  .roleDesc {
    font-size: 13px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    margin-top: 4px;
    line-height: 19px;
    max-width: 540px;
  }

  .permsTitle {
    margin-top: 22px;
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .perms {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .permChip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: ${({ theme }) => theme.colors.accent[50]};
    color: ${({ theme }) => theme.colors.accent[500]};
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;

    svg {
      color: ${({ theme }) => theme.colors.success[500]};
    }
  }

  .callout {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-top: 20px;
    padding: 12px 14px;
    background: ${({ theme }) => theme.colors.warn[50]};
    border: 1px solid ${({ theme }) => theme.colors.warn[100]};
    border-radius: 8px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.warn[700]};
    line-height: 18px;

    svg {
      color: ${({ theme }) => theme.colors.warn[700]};
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  .notificationsCallout {
    margin-bottom: 20px;
  }

  /* Branch list */
  .branchList {
    margin-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .branchRow {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    border: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    border-radius: 10px;

    &.isActive {
      background: ${({ theme }) => theme.colors.accent[50]};
      border-color: ${({ theme }) => theme.colors.accent[100]};
    }
  }

  .branchIco {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: #fff;
    color: ${({ theme }) => theme.colors.accent[500]};
    border: 1px solid ${({ theme }) => theme.colors.accent[100]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .branchRow.isActive & {
      background: ${({ theme }) => theme.colors.accent[500]};
      color: #fff;
      border-color: ${({ theme }) => theme.colors.accent[500]};
    }
  }

  .branchRow.isActive .branchIco {
    background: ${({ theme }) => theme.colors.accent[500]};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.accent[500]};
  }

  .branchMid {
    flex: 1;
    min-width: 0;
  }

  .branchName {
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .branchTag {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.accent[500]};
    color: #fff;
    font-weight: 500;
  }

  .branchAddr {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    margin-top: 2px;
  }

  /* Sessions */
  .sessionList {
    margin-top: 8px;
  }

  .sessionRow {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;

    & + .sessionRow {
      border-top: 1px solid ${({ theme }) => theme.colors.semantic.separator};
    }
  }

  .sessionIco {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: ${({ theme }) => theme.colors.accent[50]};
    color: ${({ theme }) => theme.colors.accent[500]};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .sessionMid {
    flex: 1;
    min-width: 0;
  }

  .sessionDevice {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.semantic.text.strong};
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  .sessionCurrent {
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.success[50]};
    color: ${({ theme }) => theme.colors.success[600]};
    font-weight: 500;
  }

  .sessionMeta {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.semantic.text.weak};
    margin-top: 2px;
  }

  .linkDanger {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.error[500]};
    font-weight: 500;
    font-size: 13px;
    font-family: inherit;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Mobile/tablet: collapse the two-column content + form grids and let the
     hero actions and tab bar reflow. */
  ${media.maxTablet`
    .grid {
      grid-template-columns: 1fr;
      gap: 20px;
    }
    .aside {
      position: static;
    }
  `}

  ${media.maxMobile`
    .heroRow {
      flex-wrap: wrap;
    }
    .heroActions {
      width: 100%;
    }
    .tabBar {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }
    .tabBtn {
      flex-shrink: 0;
      white-space: nowrap;
    }
    .gridTwo {
      grid-template-columns: 1fr;
    }
  `}
`;
